import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  skip,
  Subject,
  switchMap,
  take,
  takeWhile,
  tap,
  timer,
} from 'rxjs';
import { environment } from '../../environments/environment';
import { JsonResponse } from '../shared/api/backend-config';
import { ARBITRARY_CELL } from '../map/mapbox/services/layer-store.service';
import { ReportTypes } from '../user/user/user.model';
import type { MapboxGeoJSONFeature } from 'mapbox-gl';
import {
  SELECTED_POLYGON,
  SelectedCellService,
} from '../map/mapbox/services/selected-cell.service';
import { MapBoxService } from '../map/mapbox/mapbox.service';
import { ToastrService } from 'ngx-toastr';
import { ReportStatus } from './requested-reports/requested-reports.component';
import { UserAccessService } from '../user/access/user-access.service';
import { OrderEntry, OrderOutcome } from './order-report/order-report.model';
import { findReport } from './order-report/choose-type/report-type/reports-info';
import { hasStoredAccessToken } from '../user/auth-tokens';

export interface RequestPricesResponse {
  /**
   * Stripe Checkout URL, or `''` when the user's plan covered the whole order -
   * an empty string is a successful free order, not a failure.
   */
  paymentUrl: string;
  /** Ties this order to its row in `GET /report/my` while we poll for it. */
  requestId: string;
  totalPriceCents: number;
  reports: {
    reportType: ReportTypes;
    quantity: number;
    priceCents: number;
  }[];
}

export interface RequestPricesBody {
  reports: {
    location: {
      cellId: string | number;
      cellType: string | undefined;
      name: string;
    };
    type: string;
  }[];
}

export interface RequestedReport {
  url?: string;
  name: string;
  orderedAt: string;
  type: string;
  status: string;
  requestId: string;
}

export type ReportsPrices = {
  reportPricesUsd: {
    [key in ReportTypes]: number;
  };
  userAvailableCoupons:
    | {
        [key in ReportTypes]: number;
      }
    | null;
};

const PENDING_MAX_MS = 15 * 60 * 1000;

/**
 * How long to wait for an order we placed to show up in `GET /report/my`.
 *
 * Only a coupon-covered order ever waits here - a paid one leaves the app for
 * Stripe - so the row should appear on the first tick. Without a deadline the
 * absent-row case satisfied neither `takeWhile` exit, and a backend that never
 * wrote the row left the tab polling every two seconds for as long as it was
 * open, under a "Building your report" screen that would never resolve.
 */
export const TRACKED_APPEAR_MAX_MS = 2 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  public lastCreatedCellId: string | null = null;
  /**
   * The circle `lastCreatedCellId` was created for. Stepping back from review
   * and forward again shouldn't mint a second cell for a shape the visitor
   * never touched - and the location step is rebuilt on every visit, so this
   * has to live beside the id rather than in that component.
   */
  public lastCreatedCellWkt: string | null = null;
  public lastOrderedRequestId: string | null = null;

  public pollingSubj = new Subject<void>();

  /**
   * Null until the visitor picks one. It used to default to COMMUNITY_COMPASS,
   * which made step 1 open on whichever audience tab owns that report instead
   * of the first, with Continue already live for a choice nobody had made.
   */
  public lastSelectedReportType: ReportTypes | null = null;

  public pricesData: ReportsPrices | null = null;

  public isSomeReportProcessing: boolean = false;

  /** One poller at a time - see `startRequestedReportsPolling`. */
  private polling = false;

  public selectedLocation: string | null = null;
  public selectedRadius: number | null = null;
  public selectedFeature: MapboxGeoJSONFeature | null = null;

  /**
   * The chosen place moved. The order step listens so it can drop a coverage
   * verdict that belonged to the previous pin - the address search resolves
   * asynchronously inside `SearchGeocoderService`, so the map click isn't the
   * only way a location changes.
   */
  public readonly locationChanged = new Subject<void>();

  public setSelectedLocation(name: string | null): void {
    this.selectedLocation = name;
    this.locationChanged.next();
  }

  public isLocationSelected = false;

  /**
   * Where the pin sits, in map coordinates.
   *
   * The only durable record of a `pin` report's location. Those reports draw
   * no circle, so `RadiusSelectionService.lastGeometry` stays null for them,
   * and the click that drops the pin *clears* `selectedFeature` on its way
   * past - so both of the things the location step used to reconstruct its
   * state from are empty for exactly the reports whose whole subject is an
   * address. Stepping back from review found nothing picked: the marker was
   * gone and Continue was disabled, on a page whose summary had just named
   * the address.
   */
  public selectedPoint: { lng: number; lat: number } | null = null;

  public setSelectedPoint(point: { lng: number; lat: number } | null): void {
    this.selectedPoint = point;
  }

  /**
   * Whether the visitor has actually chosen somewhere, by any of the three
   * routes: a tile from the map popup, a drawn radius, or a dropped pin.
   */
  public get hasPickedLocation(): boolean {
    return !!this.selectedFeature || !!this.selectedPoint;
  }

  /**
   * What the chosen location *is*, which decides how the order references it:
   * a `cell` is an existing H3 tile (already in the processed region, ordered
   * with `'selected'`), a `radius` is a circle we have to create a cell for
   * first (ordered with `'arbitrary'`).
   */
  public locationKind: 'cell' | 'radius' = 'radius';

  /**
   * How the current order is going, for whoever is showing the review step.
   * The service only ever *reports* readiness here - it deliberately does not
   * open the report itself. Polling runs from a singleton and outlives the
   * page, so a `window.open` from in here fired on whatever route the user had
   * moved on to, and got eaten by popup blockers besides.
   */
  public readonly outcome = new BehaviorSubject<OrderOutcome>({ kind: 'idle' });

  /**
   * Where the next visit to /reports/order should open, set by whoever is
   * about to navigate there. Consumed once - see `takeOrderEntry`.
   */
  private pendingEntry: OrderEntry | null = null;

  constructor(
    private http: HttpClient,
    private selectedCellService: SelectedCellService,
    private mapboxService: MapBoxService,
    private accessService: UserAccessService,
    private toast: ToastrService
  ) {
    this.selectedCellService.selectedCellGeoId.subscribe(() => {
      this.setSelectedReportsFeature(
        this.selectedCellService.getSelectedFeature()
      );
    });

    accessService.isUserPlanUpdated
      .pipe(
        skip(2), // Skip initial emissions to get updated data
        take(1)
      )
      .subscribe(() => {
        this.startRequestedReportsPolling();
      });
  }

  // ---------------------------------------------------------------------------
  // Entering the flow
  // ---------------------------------------------------------------------------

  /**
   * Declares which step /reports/order should open on. Call this immediately
   * before navigating there; see `OrderEntry` for the cases.
   */
  public enterOrderFlow(entry: OrderEntry): void {
    this.pendingEntry = entry;

    if (entry.at !== 'type') {
      this.setSelectedReportType(entry.type);
    }

    if (entry.at === 'review' && entry.geometry === 'cell') {
      this.setSelectedReportsFeature(
        this.selectedCellService.getSelectedFeature()
      );
      this.isLocationSelected = true;
      this.locationKind = 'cell';
    } else {
      this.locationKind = 'radius';
    }
  }

  /**
   * Reads and clears the pending entry. Clearing matters: without it, a later
   * plain navigation to /reports/order would re-open a step the visitor never
   * asked for, using a location they may have long since forgotten choosing.
   */
  public takeOrderEntry(): OrderEntry {
    const entry = this.pendingEntry ?? { at: 'type' };
    this.pendingEntry = null;
    return entry;
  }

  // ---------------------------------------------------------------------------
  // Ordering
  // ---------------------------------------------------------------------------

  public getRequestedReports(): Observable<JsonResponse<RequestedReport[]>> {
    return this.http.get<JsonResponse<RequestedReport[]>>(
      `${environment.apiUrl}report/my`
    );
  }

  /**
   * Turns a drawn radius into a cell the order can reference.
   *
   * Answers 422 when the polygon falls outside the region we have data for -
   * which is why the location step calls this on Next rather than at payment
   * time. The `cellId` is needed either way, so checking early costs nothing
   * and lets us refuse a location while the map is still in front of the user.
   */
  public createLocationCell(wkt: string): Observable<JsonResponse<any>> {
    return this.http.post<JsonResponse<any>>(`${environment.apiUrl}cell`, {
      multipolygonWkt: wkt,
      name: 'reports',
      notes: 'null',
    });
  }

  public requestReport(
    type: 'arbitrary' | 'selected'
  ): Observable<JsonResponse<RequestPricesResponse>> {
    const body = this.getRequestPricesBody(type);
    return this.http
      .post<
        JsonResponse<RequestPricesResponse>
      >(`${environment.apiUrl}report/order`, body)
      .pipe(
        tap((res) => {
          const id = res?.object?.requestId ?? null;

          if (id) {
            this.lastOrderedRequestId = id;
            this.pollingSubj.next();
          }
        })
      );
  }

  /**
   * Both of these ask "can we call an authenticated endpoint yet", and the
   * honest answer is the token, not `UserAccessService.isUnauthorized`.
   *
   * That flag only flips in `setUserPlan`, once /user has answered. The
   * sign-up modal resolves earlier than that - `requestAuth()` returns on
   * `hasStoredAccessToken()` the moment it closes - so between the two there
   * is a window where the request would succeed and the flag still says no.
   * Both calls below happen inside exactly that window, on the last click of
   * an order, and both used to silently do nothing there.
   */
  private get canCallAuthedEndpoints(): boolean {
    return !this.accessService.isUnauthorized || hasStoredAccessToken();
  }

  public updateReportsPrices(): void {
    if (this.canCallAuthedEndpoints) {
      this.getReportsPrices().subscribe({
        next: (data) => (this.pricesData = data.object),
        error: (err) => console.error('Failed to load report prices', err),
      });
    }
  }

  /** The real, per-user price, or null until /report/prices has answered. */
  public priceUsdFor(type: ReportTypes): number | null {
    return this.pricesData?.reportPricesUsd?.[type] ?? null;
  }

  /** How many of this report the user's plan covers. */
  public couponsFor(type: ReportTypes): number {
    return this.pricesData?.userAvailableCoupons?.[type] ?? 0;
  }

  public getRequestPricesBody(
    type: 'arbitrary' | 'selected'
  ): RequestPricesBody {
    const isArbitrary = type === 'arbitrary';

    return {
      reports: [
        {
          location: {
            cellId: isArbitrary
              ? this.lastCreatedCellId!
              : this.selectedFeature!.id!,
            cellType: isArbitrary
              ? ARBITRARY_CELL
              : this.selectedCellService.getCellType(),
            name: isArbitrary
              ? this.selectedLocation! + ', ' + this.selectedRadius + ' mile'
              : this.selectedLocation!,
          },
          type: this.lastSelectedReportType!,
        },
      ],
    };
  }

  public setSelectedReportsFeature(feature: MapboxGeoJSONFeature | null): void {
    this.selectedFeature = feature;
  }

  public setSelectedReportType(reportType: ReportTypes): void {
    this.lastSelectedReportType = reportType;
  }

  /**
   * Whether the report being ordered summarises an area rather than a single
   * address, and so wants the trade-area circle drawn around the pin.
   *
   * Lives here because both ways of placing a pin need the same answer: the
   * map click handler in the location step, and the address search, which
   * resolves inside `SearchGeocoderService` and never passes through it. The
   * search used to render the circle unconditionally, so a `pin` report showed
   * one when you searched an address and none when you clicked the same spot.
   */
  public get selectedReportUsesRadius(): boolean {
    if (!this.lastSelectedReportType) return false;
    return findReport(this.lastSelectedReportType)?.location === 'radius';
  }

  /**
   * Forgets the selected tile, and takes its outline off the map if the map is
   * still there to take it off.
   *
   * The guard is not paranoia: this runs from `resetData`, which is called from
   * the review step - by which point the location step and its Mapbox instance
   * have been destroyed, while `MapBoxService.reportsMap` still holds the dead
   * reference. Calling into a removed map from here would throw halfway through
   * `resetData` and leave the order half-cleared, so the clearing comes first
   * and the map is best-effort.
   */
  public deselectReportsFeature(): void {
    this.selectedFeature = null;

    try {
      if (this.mapboxService.reportsMap?.getLayer(SELECTED_POLYGON)) {
        this.mapboxService.reportsMap.removeLayer(SELECTED_POLYGON);
      }
    } catch {
      // Map is gone; the layer went with it.
    }
  }

  /**
   * Polls `GET /report/my` until whatever is building has settled.
   *
   * Called from two places - once per session when the plan resolves, and
   * again by a coupon-covered order - so it guards against starting a second
   * timer. Two of these meant two requests every two seconds against the same
   * endpoint, racing to write the same `outcome`.
   */
  public startRequestedReportsPolling(): void {
    if (!this.canCallAuthedEndpoints || this.polling) return;

    this.polling = true;
    const waitingSince = Date.now();

    timer(0, 2000)
      .pipe(
        switchMap(() => this.getRequestedReports()),
        tap((data: JsonResponse<RequestedReport[]>) => {
          const reports = data.object ?? [];

          // If we have an active order, wait until /my returns the same requestId
          if (this.lastOrderedRequestId) {
            const tracked = reports.find(
              (r) => r.requestId === this.lastOrderedRequestId
            );

            if (!tracked) {
              // Still waiting for our requestId to appear in /my. Give it a
              // deadline: an absent row satisfies neither exit below, so
              // without one this polls for the life of the tab.
              if (Date.now() - waitingSince > TRACKED_APPEAR_MAX_MS) {
                this.settle({
                  kind: 'failed',
                  message:
                    'We couldn’t confirm this order. Check My reports in a ' +
                    'few minutes, or email info@map-ai.co and we’ll look.',
                });
                return;
              }

              this.isSomeReportProcessing = true;
              this.outcome.next({ kind: 'generating' });
              return;
            }

            if (this.isReportInProgress(tracked)) {
              this.isSomeReportProcessing = true;
              this.outcome.next({ kind: 'generating' });
              return;
            }

            if (tracked.status === ReportStatus.READY && tracked.url) {
              this.settle({ kind: 'ready', url: tracked.url });
              return;
            }

            if (tracked.status === ReportStatus.AUTOMATION_ERROR) {
              this.settle({
                kind: 'failed',
                message:
                  'We couldn’t build this report. You have not been charged ' +
                  'twice - email info@map-ai.co and we’ll sort it out.',
              });
              return;
            }

            // Not in progress and not ready/error (e.g. too old pending or
            // NOT_PAID) -> nothing to show, stop tracking.
            this.settle({ kind: 'idle' });
            return;
          }

          // No order of our own in flight. Track whether anything is building
          // so My reports can badge it, but say nothing about `outcome`: the
          // report being watched here belongs to an earlier visit, and
          // announcing it as "generating" put the review step into a state
          // that hides the summary and the button, so no new order could be
          // placed - and nothing in this branch ever cleared it again.
          this.isSomeReportProcessing = reports.some((report) =>
            this.isReportInProgress(report)
          );
        }),
        // Continue polling while some report is still processing
        // (and also while we are waiting for our tracked requestId to appear)
        takeWhile(
          () => this.isSomeReportProcessing || !!this.lastOrderedRequestId,
          true
        )
      )
      .subscribe({
        error: (err) => {
          console.error('Error during report polling:', err);
          this.outcome.next({
            kind: 'failed',
            message:
              'We lost track of this report. Check My reports in a minute - ' +
              'it is still being built.',
          });
          this.isSomeReportProcessing = false; // Stop polling on error
          this.polling = false;
        },
        complete: () => {
          // This will be called when takeWhile condition becomes false
          this.isSomeReportProcessing = false;
          this.polling = false;
        },
      });
  }

  private settle(outcome: OrderOutcome): void {
    this.lastOrderedRequestId = null;
    this.isSomeReportProcessing = false;
    this.outcome.next(outcome);

    if (outcome.kind === 'failed') {
      this.toast.warning(outcome.message);
    }
  }

  public isReportInProgress(report: RequestedReport): boolean {
    if (!report?.orderedAt) return false;
    const ageMs = Date.now() - new Date(report.orderedAt).getTime();

    const processStatus =
      report.status === ReportStatus.ORDERED ||
      report.status === ReportStatus.IN_PROGRESS_AUTOMATED;
    return processStatus && ageMs <= PENDING_MAX_MS;
  }

  public outlineReportCell(feature: MapboxGeoJSONFeature): void {
    if (this.mapboxService.reportsMap.getLayer(SELECTED_POLYGON)) return;

    this.mapboxService.reportsMap.addLayer({
      id: SELECTED_POLYGON,
      type: 'line',
      source: {
        type: 'geojson',
        data: feature,
      },
      paint: {
        'line-color': '#695dff',
        'line-width': 2,
      },
    });
  }

  /** Clears the order so the flow can start again from an empty state. */
  public resetData(): void {
    this.lastCreatedCellId = null;
    this.lastCreatedCellWkt = null;
    this.lastOrderedRequestId = null;
    this.isLocationSelected = false;
    this.locationKind = 'radius';
    this.selectedLocation = null;
    this.selectedPoint = null;
    this.selectedRadius = null;
    this.deselectReportsFeature();
    this.outcome.next({ kind: 'idle' });
  }

  private getReportsPrices(): Observable<JsonResponse<ReportsPrices>> {
    return this.http.get<JsonResponse<ReportsPrices>>(
      `${environment.apiUrl}report/prices`
    );
  }
}
